import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import api from '../lib/api';
import { coverSlotForPost, SLOT_DEFAULTS, avatarForAuthor } from '../lib/imagery';
import { useBrandImagesMap } from '../lib/BrandImagesContext';

function resolvePostCover(post, overrides) {
    if (post?.cover_image) return post.cover_image;
    const slot = coverSlotForPost(post);
    return (overrides && overrides[slot]) || SLOT_DEFAULTS[slot];
}

export default function BlogPage() {
    const [posts, setPosts] = useState([]);
    const [q, setQ] = useState('');
    const [tag, setTag] = useState(null);
    const { overrides } = useBrandImagesMap();

    useEffect(() => {
        api.get('/blog').then((r) => setPosts(r.data.items || [])).catch(() => {});
    }, []);

    const tags = useMemo(() => {
        const set = new Set();
        posts.forEach((p) => (p.tags || []).forEach((t) => set.add(t)));
        return Array.from(set);
    }, [posts]);

    const filtered = posts.filter((p) => {
        const okQ = q ? (p.title + ' ' + p.excerpt).toLowerCase().includes(q.toLowerCase()) : true;
        const okT = tag ? (p.tags || []).includes(tag) : true;
        return okQ && okT;
    });

    return (
        <div data-testid="blog-page">
            <section className="relative pt-32 lg:pt-36 pb-10">
                <div className="absolute inset-0 -z-10 dt-grid-overlay dt-noise pointer-events-none" />
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <span className="dt-section-eyebrow">INSIGHTS</span>
                    <h1 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">
                        Insights from <span className="dt-gradient-text">DigiTech Cloud AI</span>
                    </h1>
                    <p className="mt-4 text-muted-foreground max-w-2xl">
                        Practical perspectives on advanced software engineering, artificial intelligence, data, cloud, cybersecurity, digital transformation, and the technologies shaping governments, enterprises, and emerging markets.
                    </p>

                    <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search articles..."
                                className="pl-9 bg-white/5 border-white/10 focus-visible:ring-primary"
                                data-testid="blog-search-input"
                            />
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setTag(null)}
                                className={`text-xs font-mono px-3 py-1.5 rounded-full border transition-colors ${
                                    !tag ? 'border-primary/60 bg-primary/10 text-primary' : 'border-white/10 hover:border-white/20 text-foreground/70'
                                }`}
                                data-testid="blog-tag-all"
                            >
                                ALL
                            </button>
                            {tags.map((t) => (
                                <button
                                    key={t}
                                    onClick={() => setTag(t)}
                                    data-testid={`blog-tag-${t.toLowerCase()}`}
                                    className={`text-xs font-mono px-3 py-1.5 rounded-full border transition-colors uppercase tracking-widest ${
                                        tag === t ? 'border-primary/60 bg-primary/10 text-primary' : 'border-white/10 hover:border-white/20 text-foreground/70'
                                    }`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="pb-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map((p, i) => (
                        <motion.article
                            key={p.id}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-60px' }}
                            transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
                        >
                            <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block dt-glass rounded-2xl overflow-hidden hover:border-primary/40 transition-colors">
                                <div className="aspect-[16/10] overflow-hidden relative">
                                    <img src={resolvePostCover(p, overrides)} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[rgba(8,12,20,0.55)] via-transparent to-transparent" />
                                </div>
                                <div className="p-5">
                                    <div className="flex items-center gap-2">
                                        {(p.tags || []).slice(0, 2).map((t) => (
                                            <Badge key={t} variant="outline" className="font-mono text-[10px] tracking-[0.18em] uppercase border-primary/30 text-primary bg-primary/5">
                                                {t}
                                            </Badge>
                                        ))}
                                    </div>
                                    <h3 className="font-display mt-3 text-lg font-semibold leading-snug group-hover:text-primary line-clamp-2">
                                        {p.title}
                                    </h3>
                                    <p className="mt-2 text-sm text-foreground/75 line-clamp-3 leading-relaxed">{p.excerpt}</p>
                                    <div className="mt-4 flex items-center gap-3">
                                        {p.author ? (
                                            <img
                                                src={avatarForAuthor(p.author)}
                                                alt=""
                                                aria-hidden
                                                loading="lazy"
                                                className="h-6 w-6 rounded-full object-cover border border-white/15"
                                                style={{ filter: 'saturate(0.75)' }}
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                            />
                                        ) : null}
                                        <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                                            <Clock className="h-3 w-3" /> {p.reading_time_min} min read
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </motion.article>
                    ))}
                    {filtered.length === 0 && (
                        <div className="col-span-full text-center text-muted-foreground py-16">
                            New insights are being prepared. Please check back soon.
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
